import backend from '@common/backend';
import { IProductModelSnapshotOut } from '@common/models/Products/Product';
import { ITableDataQuery } from '@common/models/TableData';

const BASE = '/ecommerce';

type BaseID = number | string;

interface IOrdersData {
	id: number;
	quantity: number;
}

interface PostOrder {
	idempotency_key?: string;
	payment_type?: string;
	product_payment_option_id?: number;
	merchant_id?: number;
	comment?: string;
	products: IOrdersData[];
	success_url?: string;
	failure_url?: string;
	lang?: string;
}

export interface FlywireCheckoutConfirmPayload {
	checkout_session_id: number | string;
	payment_plan_id?: number | string;
	source?: string;
	success: boolean;
	order_id?: number | string;
	confirm_url: {
		method?: string;
		url: string;
	};
	payor?: Record<string, unknown>;
}

export interface PaymentMethodSessionResponse {
	provider?: string;
	payment_system?: string;
	type?: string;
	checkout_session_id?: number | string;
	expires_at?: string;
	method?: string;
	provider_session_id?: string;
	render_mode?: string;
	status?: string;
	url: string;
}

export interface ICreateProduct {
	name: string;
	description?: string;
	category_id: number;
	currency?: string;
	price?: number;
	is_published?: boolean;
	attribute_values?: { product_attribute_id: number; value: string }[];
	category?: Pick<IProductModelSnapshotOut, 'category'>;
	c_g_counselors?: Pick<IProductModelSnapshotOut, 'c_g_counselors'>[];
	c_g_counselor_ids?: number[];
	c_g_partners?: Pick<IProductModelSnapshotOut, 'c_g_partners'>[];
	c_g_partner_ids?: number[];
	c_g_group_consultation_ids?: number[];
}

export interface IUpdateProduct {
	id: BaseID;
	payload: ICreateProduct;
}

export const getMerchants = () => backend.get(`${BASE}/merchants`);

export const getProducts = (query: ITableDataQuery) =>
	backend.get(`${BASE}/products`, { params: { ...query } });

export const getProductCategories = (query: ITableDataQuery) =>
	backend.get(`${BASE}/categories`, { params: { ...query } });

export const getProductCurrencies = (query: ITableDataQuery) =>
	backend.get(`${BASE}/currencies`, { params: { ...query } });

export const getProduct = async ({ id }: { id: BaseID }) => backend.get(`${BASE}/products/${id}`);

export const createProduct = async (paylod: ICreateProduct) =>
	backend.post(`${BASE}/products`, paylod);

export const getProductAtributes = async (query: { category_ids: number[] }) =>
	backend.get(`${BASE}/products/attributes`, { params: { ...query } });

export const deleteProduct = async ({ id }: { id: BaseID }) =>
	backend.delete(`${BASE}/products/${id}`);

export const updateProduct = async ({ id, payload }: IUpdateProduct) =>
	backend.patch(`${BASE}/products/${id}`, payload);

export const postOrders = (data: PostOrder) => backend.post(`${BASE}/orders`, data);

// TODO transfer call to model when products table logic is added to BE
export const addProductToUser = (userId: number, productId: number, data?: any) =>
	backend.post(`${BASE}/users/${userId}/products/${productId}`, data);

export const getOrders = async (query: ITableDataQuery) =>
	backend.get(`${BASE}/orders`, {
		params: query,
	});

export const getPaymentsHistory = (query: ITableDataQuery) =>
	backend.get(`${BASE}/payments/history`, {
		params: query,
	});

export const getPaymentPlans = (query: ITableDataQuery) =>
	backend.get(`${BASE}/payment-plans`, {
		params: query,
	});

export const confirmFlywireCheckout = (data: FlywireCheckoutConfirmPayload) =>
	backend.post(`${BASE}/payments/flywire/checkout/confirm`, data);

export const cancelPaymentPlan = (id: BaseID) =>
	backend.post(`${BASE}/payment-plans/${id}/cancel`, {});

export const payRemainingPaymentPlan = (id: BaseID) =>
	backend.post(`${BASE}/payment-plans/${id}/pay-remaining`, {});

export const getPaymentPlanPaymentMethod = (id: BaseID): Promise<any> =>
	backend.post(`${BASE}/payment-plans/${id}/payment-method`, {});

export const confirmPaymentPlanPaymentMethod = (
	id: BaseID,
	data: FlywireCheckoutConfirmPayload,
) => backend.post(`${BASE}/payment-plans/${id}/payment-method/confirm`, data);

export const getCreditCustomerOnboarding = (userId: BaseID) =>
	backend.get(`${BASE}/credit-customers/${userId}/onboarding`);
